<template>
  <v-autocomplete
    v-model="model"
    :placeholder="placeholder || ''"
    :clearable="clearable"
    :no-filter="effectiveNoFilter"
    :filter="resolveItemFilter"
    :hide-selected="hideSelected"
    :error-messages="errors"
    :multiple="multiple"
    :disabled="disabled"
    :readonly="readonly"
    :dark="darkEnabled"
    :items="options"
    :dense="dense"
    :label="label"
    :solo="solo"
    :loading="loading"
    outlined
    rounded
    :search-input.sync="search"
    flat
    :return-object="returnObject"
    @input="onChange"
    @blur="onBlur"
    @focus="onFieldFocus"
    @click.native="onFieldClick"
    :chips="chips && !multiple"
    class="input-autocomplete"
    :hint="hint"
    :persistent-hint="persistenthint"
    :menu-props="menuProps"
  >
    <template v-if="multiple" v-slot:selection="{ item, index }">
      <template v-if="isMultiCondensed">
        <div v-if="index === 0" class="card card--multi-summary">
          {{ selectionSummaryText }}
        </div>
      </template>
      <div v-else class="card">
        {{ getItemText(item) }}
      </div>
    </template>
    <template v-slot:item="{ item }" v-if="useTemplate"
      ><slot name="item" :item="item">{{ item.text }}</slot></template
    >
  </v-autocomplete>
</template>

<script>
import { mapGetters } from "vuex";
import * as _ from "lodash";
export default {
  name: "FormAutocomplete",
  props: {
    options: {
      type: Array,
      default: () => [],
      required: false
    },
    value: {
      type: [Array, String, Object, Number, Boolean],
      default: "",
      required: false
    },
    label: {
      type: String,
      default: "",
      required: false
    },
    placeholder: {
      type: String,
      default: "",
      required: false
    },
    loading: {
      type: Boolean,
      default: false,
      required: false
    },
    dense: {
      type: Boolean,
      default: true,
      required: false
    },
    solo: {
      type: Boolean,
      default: false,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    readonly: {
      type: Boolean,
      default: false,
      required: false
    },
    clearable: {
      type: Boolean,
      default: false,
      required: false
    },
    noFilter: {
      type: Boolean,
      default: false,
      required: false
    },
    hideSelected: {
      type: Boolean,
      default: false,
      required: false
    },
    multiple: {
      type: Boolean,
      default: false,
      required: false
    },
    errorMessage: {
      type: [Object],
      default: () => ({}),
      required: false
    },
    validation: {
      type: Object,
      default: () => null,
      required: false
    },
    chips: {
      type: Boolean,
      default: false,
      required: false
    },
    useTemplate: {
      type: Boolean,
      default: false,
      required: false
    },
    returnObject: {
      type: Boolean,
      default: false,
      required: false
    },
    validar: {
      type: Boolean,
      default: true,
      required: false
    },
    hint: {
      type: String,
      default: "",
      required: false
    },
    persistenthint: {
      type: Boolean,
      default: false,
      required: false
    },
    searchOnFirstInteraction: {
      type: Boolean,
      default: false,
      required: false
    },
    itemsFilter: {
      type: Function,
      default: null,
      required: false
    }
  },
  data: () => ({
    visibled: false,
    model: "",
    onSearchDebounced: null,
    search: "",
    firstInteractionSearchDone: false
  }),
  created() {
    this.model = this.value;
    this.onSearchDebounced = _.debounce(this.onSearch, 500);
  },
  watch: {
    value: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal != oldVal && newVal != this.model) {
          this.model = newVal;
          this.$emit("deletedContent");
        }
      }
    },
    search: {
      handler(newVal, oldVal) {
        // TODO verificar motivo do search ficar limpando e repopulando sozinho
        if (newVal === oldVal) return;
        if (!newVal && this.model) return;
        const preventSearch =
          (this.model && this.model.value !== newVal) || !this.model;
        if (preventSearch) {
          this.onSearchDebounced();
        }
      }
    },
    validation: {
      deep: true,
      handler(newVal) {
        this.touched = newVal.$dirty;
      }
    }
  },
  computed: {
    ...mapGetters(["darkEnabled"]),
    menuProps() {
      return this.darkEnabled
        ? { contentClass: "form-autocomplete-menu-dark" }
        : {};
    },
    effectiveNoFilter() {
      return this.noFilter && !this.itemsFilter;
    },
    isMultiCondensed() {
      return (
        this.multiple && Array.isArray(this.model) && this.model.length > 2
      );
    },
    selectionSummaryText() {
      if (!this.isMultiCondensed) {
        return "";
      }
      const labels = this.model.slice(0, 2).map(v => this.getItemText(v));
      const extra = this.model.length - 2;
      const suffix = extra === 1 ? "+1 item" : `+${extra} itens`;
      return `${labels[0]}, ${labels[1]}, ${suffix}`;
    },
    errors() {
      if (this.validation && (this.validation.$dirty || this.touched)) {
        let errorsMess = [];
        if (
          this.validation.required != undefined &&
          !this.validation.required &&
          this.validar
        ) {
          errorsMess.push(this.errorMessage["required"] || "Campo obrigatório");
        }
        return errorsMess;
      }
      return [];
    }
  },
  methods: {
    getItemText(item) {
      if (item == null) {
        return "";
      }
      if (typeof item === "object") {
        return item.text != null ? item.text : String(item.value);
      }
      const opt = (this.options || []).find(o => o && o.value === item);
      return opt && opt.text != null ? opt.text : String(item);
    },
    onChange(value) {
      this.$emit("change", value);
      this.$emit("input", value);
      this.checkValidation();
    },
    onBlur() {
      this.$emit("blur", this.model);
      this.checkValidation();
    },
    onFieldFocus(event) {
      this.$emit("focus", event);
      this.tryFirstInteractionSearch();
    },
    onFieldClick() {
      this.tryFirstInteractionSearch();
    },
    tryFirstInteractionSearch() {
      if (
        !this.searchOnFirstInteraction ||
        this.firstInteractionSearchDone ||
        this.disabled ||
        this.readonly
      ) {
        return;
      }
      this.firstInteractionSearchDone = true;
      this.$emit("search", "");
    },
    checkValidation() {
      if (this.validation) {
        this.validation.$touch();
        this.touched = true;
      }
    },
    onSearch() {
      this.$emit("search", this.search || "");
    },
    resolveItemFilter(item, query, itemText) {
      if (this.itemsFilter) {
        return this.itemsFilter(item, query, itemText);
      }
      if (this.noFilter) {
        return true;
      }
      const haystack = (itemText != null ? itemText : "")
        .toString()
        .toLowerCase();
      const needle = (query != null ? query : "").toString().toLowerCase();
      return haystack.indexOf(needle) > -1;
    }
  }
};
</script>

<style scoped>
.card {
  display: flex;
  align-items: center;
  background-color: #e0e0e0;
  color: rgba(0, 0, 0, 0.87) !important;
  font-size: 14px;
  height: 32px;
  margin: 4px;
  padding: 0 12px;
  border-radius: 16px;
}

.card--multi-summary {
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  line-height: 32px;
}
</style>

<style>
.input-autocomplete {
  text-align: left;
}

.v-menu__content.form-autocomplete-menu-dark {
  background: var(--dark-surface, #1d1d1d) !important;
}

.v-menu__content.form-autocomplete-menu-dark .v-list {
  background: var(--dark-surface, #1d1d1d) !important;
}

.v-menu__content.form-autocomplete-menu-dark .v-select-list.v-card {
  background: var(--dark-surface, #1d1d1d) !important;
  box-shadow: none !important;
}

.v-menu__content.form-autocomplete-menu-dark
  .v-list
  .v-list-item:not(.v-list-item--disabled) {
  color: rgba(255, 255, 255, 0.87) !important;
}

.v-menu__content.form-autocomplete-menu-dark .v-list-item__title,
.v-menu__content.form-autocomplete-menu-dark .v-list-item__subtitle {
  color: rgba(255, 255, 255, 0.87) !important;
}

.v-menu__content.form-autocomplete-menu-dark .v-list-item__content,
.v-menu__content.form-autocomplete-menu-dark .v-list-item__content p,
.v-menu__content.form-autocomplete-menu-dark .v-list-item__content small,
.v-menu__content.form-autocomplete-menu-dark .v-list-item > div,
.v-menu__content.form-autocomplete-menu-dark .v-list-item p,
.v-menu__content.form-autocomplete-menu-dark .v-list-item small {
  color: rgba(255, 255, 255, 0.87) !important;
}

.v-menu__content.form-autocomplete-menu-dark .v-divider {
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.v-menu__content.form-autocomplete-menu-dark input {
  color: rgba(255, 255, 255, 0.87) !important;
  caret-color: rgba(255, 255, 255, 0.87) !important;
}

.v-menu__content.form-autocomplete-menu-dark .v-simple-checkbox .v-icon {
  color: rgba(255, 255, 255, 0.87) !important;
}
</style>
